import uuidv4 from '@/lib/uuid'
import {
  Badge,
  Box,
  Button,
  Combobox,
  Highlight,
  Modal,
  Text,
  TextInput,
  useCombobox,
} from '@mantine/core'
import { getHotkeyHandler, useDisclosure } from '@mantine/hooks'
import { IconSearch } from '@tabler/icons-react'
import { useRouteContext, useRouter } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { extractRoles } from '@/utils/routeGuards'
import { MenuGroup, MenuItemWithAllowedRoles, menus, MenuSection } from './menu'

type MenuItemOption = MenuItemWithAllowedRoles & {
  id: string
}
export function SearchMenu() {
  const router = useRouter()
  const menuItems: MenuItemOption[] = []
  const context = useRouteContext({ from: '/_auth' })

  const renderMenuItem = (item: MenuItemWithAllowedRoles) => {
    let hasPermission = true
    const roles = extractRoles(item.allowedRoles || [])
    if (context.auth && roles) {
      hasPermission = context.auth.hasRequiredRole(roles)
    }
    if (hasPermission) {
      menuItems.push({ ...item, id: uuidv4() })
    }
  }

  const renderMenuSection = (section: MenuSection) => {
    section.menus.forEach((item) => {
      renderMenuItem(item)
    })
  }

  const renderMenuGroup = (group: MenuGroup) => {
    group.sections.forEach((section) => renderMenuSection(section))
  }
  menus.forEach((group) => renderMenuGroup(group))

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  })
  const [value, setValue] = useState('')
  const shouldFilterOptions = !menuItems.some((item) => item.label === value)
  const filteredOptions = shouldFilterOptions
    ? menuItems.filter((item) =>
        item.label.toLowerCase().includes(value.toLowerCase().trim())
      )
    : menuItems

  const options = filteredOptions.map((item, index) => (
    <Combobox.Option value={item.id} key={index}>
      <Highlight highlight={value} size="lg">
        {item.label}
      </Highlight>
    </Combobox.Option>
  ))

  const keyHandler = getHotkeyHandler([['mod+K', () => open()]])

  const [opened, { open, close }] = useDisclosure(false)

  useEffect(() => {
    document.body.addEventListener('keydown', keyHandler)
    return () => {
      document.body.removeEventListener('keydown', keyHandler)
    }
  }, [keyHandler])

  useEffect(() => {
    combobox.selectFirstOption()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  return (
    <>
      <Button
        variant="default"
        onClick={open}
        leftSection={<IconSearch size="1rem" stroke={1.5} />}
        rightSection={
          <Badge color="gray" visibleFrom="sm">
            Ctrl + K
          </Badge>
        }
        px={{ base: 'xs', sm: 'md' }}
      >
        <Text pr={{ base: 0, sm: 30 }} visibleFrom="sm">
          ค้นหา
        </Text>
      </Button>
      <Modal opened={opened} onClose={close} withCloseButton={false}>
        <Box h={500}>
          <Combobox
            data-autofocus
            onOptionSubmit={(optionValue) => {
              router.navigate({
                to: menuItems.find((item) => item.id === optionValue)?.path,
              })
              combobox.closeDropdown()
              close()
            }}
            withinPortal={false}
            store={combobox}
          >
            <Combobox.Target>
              <TextInput
                placeholder="Search..."
                size="md"
                value={value}
                leftSection={<IconSearch size="1rem" stroke={1.5} />}
                onChange={(event) => {
                  setValue(event.currentTarget.value)
                  combobox.updateSelectedOptionIndex()
                  combobox.openDropdown()
                }}
                onClick={() => combobox.openDropdown()}
                onFocus={() => combobox.openDropdown()}
                onBlur={() => combobox.closeDropdown()}
              />
            </Combobox.Target>

            <Combobox.Dropdown>
              <Combobox.Options>
                {options.length === 0 ? (
                  <Combobox.Empty>Nothing found</Combobox.Empty>
                ) : (
                  options
                )}
              </Combobox.Options>
            </Combobox.Dropdown>
          </Combobox>
        </Box>
      </Modal>
    </>
  )
}
